import React from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";

const IssueExportButtons = ({ data, filename = "Stock_Issue_Report" }) => {

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr; 
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}-${m}-${y}`;
    };

    const today = formatDate(new Date());
    const totalQty = data.reduce((sum, row) => sum + (Number(row.quantity_issued) || 0), 0);

    const handlePrint = () => {
        const win = window.open('', '_blank');
        const rows = data.map(row => `
            <tr>
                <td>${formatDate(row.issue_date)}</td>
                <td>${row.cut_order_no || ""}</td>
                <td>${row.buyer_name || "N/A"}</td>
                <td>${row.style_name || "N/A"}</td>
                <td>${row.item_name ? `${row.item_name} ${row.item_size !== '-' ? `(${row.item_size})` : ''}` : "-"}</td>
                <td>${row.colour_name || "-"}</td> 
                <td>${row.quantity_issued || 0}</td>
            </tr>
        `).join("");

        win.document.write(`
            <html>
                <head>
                    <title>&nbsp;</title> 
                    <style>
                        @page { size: landscape; margin: 0mm; }
                        body { font-family: 'Segoe UI', Tahoma, sans-serif; color: #333; margin: 0; padding: 15mm; }
                        .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #444; padding-bottom: 10px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th { background-color: #f2f2f2; font-weight: bold; text-transform: uppercase; font-size: 11px; border: 1px solid #ccc; padding: 10px; }
                        td { border: 1px solid #ccc; padding: 10px; text-align: left; font-size: 12px; }
                        .total-row { background-color: #eee; font-weight: bold; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h2 style="margin:5px 0; color:#333;">${filename.replace(/_/g, ' ')}</h2>
                        <p>Date Generated: ${today}</p>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Date</th><th>Order No</th><th>Customer</th><th>Style</th><th>Item (Size)</th><th>Color</th><th>Qty Issued</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${rows}
                            <tr class="total-row">
                                <td colspan="6" style="text-align: right;">OVERALL TOTAL:</td>
                                <td>${totalQty}</td>
                            </tr>
                        </tbody>
                    </table>
                </body>
            </html>
        `);
        win.document.close();
        setTimeout(() => { win.focus(); win.print(); win.close(); }, 250);
    };

    const downloadPDF = () => {
        const doc = new jsPDF("l", "pt", "a4");
        doc.setFontSize(16);
        doc.text(`${filename.replace(/_/g, ' ')}`, 40, 45);
        doc.setFontSize(10);
        doc.text(`Date: ${today}`, 730, 45);
        const tableColumn = ["Date", "Order No", "Customer", "Style", "Item (Size)", "Color", "Qty Issued"];
        const tableRows = data.map(row => [
            formatDate(row.issue_date),
            row.cut_order_no,
            row.buyer_name || "N/A",
            row.style_name || "N/A",
            row.item_name ? `${row.item_name} ${row.item_size !== '-' ? `(${row.item_size})` : ''}` : "N/A",
            row.colour_name || "-", 
            row.quantity_issued
        ]);
        tableRows.push([
            { content: 'OVERALL TOTAL DETAILS', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: totalQty.toString(), styles: { fontStyle: 'bold' } }
        ]);
        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 65,
            theme: 'grid',
            styles: { fontSize: 9 },
            headStyles: { fillColor: [40, 40, 40] }
        });
        doc.save(`${filename}_${today}.pdf`);
    };

    const downloadCSV = () => {
        const headers = ["Date", "Order No", "Customer", "Style", "Item (Size)", "Color", "Quantity"];
        const rows = data.map(row => [
            `"${formatDate(row.issue_date)}\t"`, `"${row.cut_order_no}"`, `"${row.buyer_name || "N/A"}"`,
            `"${row.style_name || "N/A"}"`, `"${row.item_name} ${row.item_size !== '-' && row.item_size ? `(${row.item_size})` : ""}"`,
            `"${row.colour_name || "-"}"`, row.quantity_issued
        ]);
        rows.push(["", "", "", "", "", "OVERALL TOTAL", totalQty]);
        const csvContent = "\uFEFF" + [headers, ...rows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${filename}_${today}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="flex gap-2">
            <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-black text-white rounded-lg transition-all shadow-md font-medium text-sm">
                <span>🖨️</span> Print
            </button>
            <button onClick={downloadPDF} className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg transition-all shadow-md font-medium text-sm">
                <span>📄</span> PDF
            </button>
            <button onClick={downloadCSV} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all shadow-md font-medium text-sm">
                <span>📥</span> CSV/Excel
            </button>
        </div>
    );
};

export default IssueExportButtons;